import { useEffect, useRef } from "react";
import { ogImageFor } from "@/seo/ogImage";

interface SEOProps {
  title: string;
  description?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: "summary" | "summary_large_image";
  noindex?: boolean;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleAuthor?: string;
}

/**
 * Hook for dynamically managing SEO meta tags
 * Updates document title and meta tags for search engines and social sharing
 */
export function useSEO({
  title,
  description,
  canonicalUrl,
  ogImage: ogImageProp,
  ogType = "website",
  twitterCard = "summary_large_image",
  noindex = false,
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor,
}: SEOProps) {
  const ogImage = ogImageProp ?? ogImageFor(canonicalUrl);
  // During the build-time prerender there is no DOM: record the page metadata instead.
  if (typeof window === "undefined") {
    (globalThis as { __BV_SEO__?: unknown }).__BV_SEO__ = { title, description, canonicalUrl, ogImage, ogType, noindex, articlePublishedTime, articleModifiedTime, articleAuthor };
  }
  const originalTitle = useRef<string | null>(null);
  const addedTags = useRef<HTMLElement[]>([]);

  useEffect(() => {
    // Store original title for cleanup
    if (originalTitle.current === null) {
      originalTitle.current = document.title;
    }

    // Update document title
    document.title = title;

    // Helper to update or create meta tags
    const updateMetaTag = (
      property: string,
      content: string,
      isName = false
    ) => {
      const selector = isName
        ? `meta[name="${property}"]`
        : `meta[property="${property}"]`;
      let tag = document.querySelector(selector) as HTMLMetaElement;

      if (tag) {
        tag.setAttribute("content", content);
      } else {
        tag = document.createElement("meta");
        if (isName) {
          tag.setAttribute("name", property);
        } else {
          tag.setAttribute("property", property);
        }
        tag.setAttribute("content", content);
        document.head.appendChild(tag);
        addedTags.current.push(tag);
      }
    };

    // Helper to update or create link tags
    const updateLinkTag = (rel: string, href: string) => {
      let tag = document.querySelector(
        `link[rel="${rel}"]`
      ) as HTMLLinkElement;

      if (tag) {
        tag.setAttribute("href", href);
      } else {
        tag = document.createElement("link");
        tag.setAttribute("rel", rel);
        tag.setAttribute("href", href);
        document.head.appendChild(tag);
        addedTags.current.push(tag);
      }
    };

    // Update meta description
    if (description) {
      updateMetaTag("description", description, true);
      updateMetaTag("og:description", description);
      updateMetaTag("twitter:description", description);
    }

    // Update Open Graph tags
    updateMetaTag("og:title", title);
    updateMetaTag("og:type", ogType);
    if (ogImage) {
      updateMetaTag("og:image", ogImage);
      updateMetaTag("twitter:image", ogImage);
    }
    if (canonicalUrl) {
      updateMetaTag("og:url", canonicalUrl);
      updateLinkTag("canonical", canonicalUrl);
    }

    // OG image dimensions
    if (ogImage) {
      updateMetaTag("og:image:width", "1200");
      updateMetaTag("og:image:height", "630");
    }

    // Update Twitter Card tags
    updateMetaTag("twitter:card", twitterCard, true);
    updateMetaTag("twitter:title", title, true);

    // Article-specific meta tags
    if (articlePublishedTime) {
      updateMetaTag("article:published_time", articlePublishedTime);
    }
    if (articleModifiedTime) {
      updateMetaTag("article:modified_time", articleModifiedTime);
    }
    if (articleAuthor) {
      updateMetaTag("article:author", articleAuthor);
    }

    // Add site-wide Open Graph properties
    updateMetaTag("og:site_name", "Beatview");
    updateMetaTag("og:locale", "en_US");

    // Handle robots directive
    if (noindex) {
      updateMetaTag("robots", "noindex, nofollow", true);
    } else {
      // Explicitly allow indexing (best practice for SEO)
      updateMetaTag("robots", "index, follow", true);
    }

    // Cleanup on unmount
    return () => {
      if (originalTitle.current) {
        document.title = originalTitle.current;
      }
      // Remove tags we added
      addedTags.current.forEach((tag) => {
        if (tag.parentNode) {
          tag.parentNode.removeChild(tag);
        }
      });
      addedTags.current = [];
    };
  }, [title, description, canonicalUrl, ogImage, ogType, twitterCard, noindex, articlePublishedTime, articleModifiedTime, articleAuthor]);
}
